import catalog from "@/config/menu-permissions.json";

export const normalizeName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();
const expand = (node) =>
  node.type === "page"
    ? {
        ...node,
        taskGroups: node.taskGroups.map((group) => ({
          ...group,
          actions: group.actions.map((action) => ({
            ...catalog.actions[action.action],
            ...action,
            id: action.id || action.action,
          })),
        })),
      }
    : { ...node, children: node.children.map(expand) };
export const permissionSections = catalog.sections.map((section) => ({
  ...section,
  children: section.children.map(expand),
}));
const flatten = (nodes) =>
  nodes.flatMap((node) =>
    node.type === "page" ? [node] : flatten(node.children),
  );
export const permissionPages = permissionSections.flatMap((section) =>
  flatten(section.children),
);
export const previewSchemaVersion = catalog.schemaVersion;
export const previewStorageKey = (roleId) =>
  `hrd.permission-preview.v${previewSchemaVersion}.${roleId}`;
export const pageActions = (page) =>
  page.taskGroups.flatMap((group) => group.actions);
export const roleRestriction = (action, roleName) =>
  action.requiredRoles &&
  !action.requiredRoles.some(
    (name) => normalizeName(name) === normalizeName(roleName),
  )
    ? `Current workflow requires ${action.requiredRoles.join(" or ")}.`
    : "";
const matchedPermission = (role, names) =>
  names
    .map((name) =>
      (role.role_menu_permissions || []).find(
        (permission) =>
          normalizeName(permission.menuItem?.menuItemName) ===
          normalizeName(name),
      ),
    )
    .find(Boolean);

export function makeRolePreview(role) {
  return Object.fromEntries(
    permissionPages.map((page) => {
      const permission = matchedPermission(role, page.menuNames);
      const view = !!permission?.canRead;
      return [
        page.id,
        {
          view,
          actions: Object.fromEntries(
            pageActions(page).map((action) => {
              const source = action.menuNames
                ? matchedPermission(role, action.menuNames)
                : permission;
              return [
                action.id,
                view &&
                  !roleRestriction(action, role.roleName) &&
                  !!(action.field && source?.[action.field]),
              ];
            }),
          ),
          scope: page.recordScope
            ? permission?.[page.recordScope.field]
              ? "others"
              : "assigned"
            : null,
        },
      ];
    }),
  );
}

export function normalizeSavedPreview(saved, baseline, roleName) {
  if (!saved || saved.schemaVersion !== previewSchemaVersion || !saved.pages)
    return baseline;
  return Object.fromEntries(
    permissionPages.map((page) => {
      const state = saved.pages[page.id];
      if (!state || typeof state.view !== "boolean")
        return [page.id, baseline[page.id]];
      return [
        page.id,
        {
          view: state.view,
          actions: Object.fromEntries(
            pageActions(page).map((action) => [
              action.id,
              state.view &&
                !roleRestriction(action, roleName) &&
                (typeof state.actions?.[action.id] === "boolean"
                  ? state.actions[action.id]
                  : baseline[page.id].actions[action.id]),
            ]),
          ),
          scope: page.recordScope?.options.some(
            (option) => option.id === state.scope,
          )
            ? state.scope
            : baseline[page.id].scope,
        },
      ];
    }),
  );
}

export function previewChanges(before, after) {
  return permissionPages
    .map((page) => {
      const previous = before[page.id],
        next = after[page.id];
      if (!previous || !next) return null;
      const changes = [];
      if (previous.view !== next.view)
        changes.push({ id: "view", label: "View page", enabled: next.view });
      pageActions(page).forEach((action) => {
        if (previous.actions[action.id] !== next.actions[action.id])
          changes.push({
            id: action.id,
            label: action.label,
            enabled: next.actions[action.id],
          });
      });
      if (previous.scope !== next.scope)
        changes.push({
          id: "scope",
          label: "Record scope",
          value: page.recordScope.options.find(
            (option) => option.id === next.scope,
          ).label,
        });
      return changes.length
        ? { id: page.id, label: page.label, changes }
        : null;
    })
    .filter(Boolean);
}
